/**
 * DocumentsPage — Central document repository + Forms library.
 * Tabs: Browse · Quick Forms · Expiring Soon · Archive
 * Features:
 *  - Drag-drop upload with category/tags/expiry/description/Quick-Form toggle
 *  - Search, category filter, tag filter, status filter
 *  - Per-row preview (PDF/image inline), download, edit metadata, archive/restore, new version, version history
 *  - Quick Forms bar at top — one-click downloads of starred forms
 *  - Expiring Soon tab — colour-coded urgency (≤7 critical · ≤30 warning · ≤90 info)
 *  - Mobile-friendly grid + responsive tables
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  FileText, Upload, Download, Eye, Archive, Trash2, Edit3, Search,
  History, Star, FolderOpen, AlertCircle, Clock, Filter, RotateCcw,
  FileSpreadsheet, FileImage, File, Link2, Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import FileExplorer from '../components/documents/FileExplorer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MIME_ICON = (mime = '') => {
  if (mime.startsWith('image/')) return FileImage;
  if (mime === 'application/pdf') return FileText;
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return FileSpreadsheet;
  if (mime.includes('word') || mime.includes('text')) return FileText;
  return File;
};

const URGENCY_BADGE = (urg) =>
  urg === 'critical' ? 'bg-red-100 text-red-800 border-red-200'
  : urg === 'warning' ? 'bg-amber-100 text-amber-800 border-amber-200'
  : 'bg-blue-100 text-blue-800 border-blue-200';

const downloadBase64 = (b64, filename, mime) => {
  try {
    const bytes = atob(b64);
    const buf = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
    const blob = new Blob([buf], { type: mime || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'document'; document.body.appendChild(a);
    a.click(); a.remove(); URL.revokeObjectURL(url);
  } catch (e) {
    toast.error('Download failed');
  }
};

const DocumentsPage = () => {
  const { hasPermission, hasDocumentScope, allowedDocumentScopes } = useAuth();
  const canEdit = hasPermission('documents', 'create') || hasPermission('documents', 'edit');
  const canDelete = hasPermission('documents', 'delete');
  const canArchive = hasPermission('documents', 'archive') || canEdit;

  const accessibleScopes = useMemo(() => allowedDocumentScopes('view'), [allowedDocumentScopes]);
  const uploadScopes = useMemo(() => allowedDocumentScopes('upload'), [allowedDocumentScopes]);

  const [scope, setScope] = useState('all'); // 'all' | 'schools' | 'women' | 'general'
  const [scopesMeta, setScopesMeta] = useState([]);

  const [tab, setTab] = useState('browse');
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [quickForms, setQuickForms] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [summary, setSummary] = useState(null);

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [versionTarget, setVersionTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);

  // Drive-style explorer state
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [view, setView] = useState('grid');
  const [shareTarget, setShareTarget] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // { type: 'archive'|'delete', doc }
  const [uploadFolderId, setUploadFolderId] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const searching = !!search;
      const [d, c, q, e, s, sc, f] = await Promise.all([
        axios.get(`${API_URL}/api/documents`, {
          params: {
            ...(search ? { q: search } : {}),
            ...(filterCategory !== 'all' ? { category: filterCategory } : {}),
            ...(scope !== 'all' ? { scope } : {}),
            status: filterStatus,
            ...(!searching ? { folder_id: currentFolderId || 'root' } : {}),
          },
        }),
        axios.get(`${API_URL}/api/documents/categories`),
        axios.get(`${API_URL}/api/documents/quick-forms`),
        axios.get(`${API_URL}/api/documents/expiring`, { params: { days: 90 } }),
        axios.get(`${API_URL}/api/documents/summary`),
        axios.get(`${API_URL}/api/documents/scopes`),
        axios.get(`${API_URL}/api/documents/folders`),
      ]);
      setDocs(d.data || []);
      setCategories(c.data || []);
      setQuickForms(q.data || []);
      setExpiring(e.data || []);
      setSummary(s.data || null);
      setScopesMeta(sc.data || []);
      setFolders(f.data || []);
    } catch (err) {
      toast.error('Failed to load documents');
    }
  }, [search, filterCategory, filterStatus, scope, currentFolderId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const visibleDocs = useMemo(
    () => docs.filter((d) => (filterStatus !== 'archived' ? d.status !== 'archived' : d.status === 'archived')),
    [docs, filterStatus]
  );

  const onDownload = async (doc) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/${doc.id}/download`);
      downloadBase64(res.data.file_data, res.data.file_name, res.data.file_type);
      toast.success('Downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const onPreview = async (doc) => {
    if (!doc.previewable) {
      toast.info('Preview not supported — downloading instead');
      return onDownload(doc);
    }
    try {
      const res = await axios.get(`${API_URL}/api/documents/${doc.id}/download`);
      setPreviewTarget({
        ...doc,
        dataUrl: `data:${res.data.file_type};base64,${res.data.file_data}`,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
  };

  const onArchive = async (doc) => {
    try {
      await axios.post(`${API_URL}/api/documents/${doc.id}/archive`);
      toast.success('Archived');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
  };
  const onRestore = async (doc) => {
    try {
      await axios.post(`${API_URL}/api/documents/${doc.id}/restore`);
      toast.success('Restored');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    }
  };
  const onDelete = async (doc) => {
    try {
      await axios.delete(`${API_URL}/api/documents/${doc.id}`);
      toast.success('Deleted');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const categoryLabel = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.key] = c.label; });
    return m;
  }, [categories]);

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <FolderOpen className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-heading font-bold">Documents & Forms</h1>
            <p className="text-sm text-muted-foreground">Central repository for licenses, policies, forms and reports.</p>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => { setUploadFolderId(currentFolderId); setUploadOpen(true); }} data-testid="upload-doc-btn">
            <Upload className="h-4 w-4 mr-2" /> Upload document
          </Button>
        )}
      </div>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total documents" value={summary.total} icon={FileText} tone="primary" testid="stat-total" />
          <StatCard label="Uploaded this week" value={summary.recent_uploads} icon={Upload} tone="emerald" testid="stat-recent" />
          <StatCard label="Expiring (30d)" value={summary.expiring_soon} icon={Clock} tone="amber" testid="stat-expiring" />
          <StatCard label="Expired" value={summary.expired} icon={AlertCircle} tone="red" testid="stat-expired" />
          <StatCard label="Archived" value={summary.archived} icon={Archive} tone="slate" testid="stat-archived" />
        </div>
      )}

      {/* Scope tabs — Schools / Women Development / General */}
      <ScopeTabs
        scope={scope}
        onChange={setScope}
        scopesMeta={scopesMeta}
        accessibleScopes={accessibleScopes}
        byScope={summary?.by_scope || {}}
      />

      {/* Quick Forms bar */}
      {quickForms.length > 0 && (
        <Card className="bg-gradient-to-r from-amber-50/60 to-green-50/60 dark:from-amber-900/20 dark:to-green-900/20 border-amber-200/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500 fill-amber-400" /> Quick Forms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {quickForms.map((f) => (
                <Button
                  key={f.id} variant="outline" size="sm" onClick={() => onDownload(f)}
                  className="bg-background/80"
                  data-testid={`quick-form-${f.id}`}
                  title={f.description || ''}
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> {f.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="browse" data-testid="tab-browse">Browse</TabsTrigger>
          <TabsTrigger value="expiring" data-testid="tab-expiring">
            Expiring Soon {expiring.length > 0 && <Badge variant="outline" className="ml-1.5">{expiring.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive">Archive</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="space-y-4 pt-4">
          <Card>
            <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, description, tag or filename…"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    className="pl-9" data-testid="doc-search"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger data-testid="doc-category-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label} ({c.count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="doc-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <FileExplorer
            folders={folders}
            docs={visibleDocs}
            currentFolderId={currentFolderId}
            onNavigate={setCurrentFolderId}
            searchText={search}
            view={view}
            setView={setView}
            canEdit={canEdit}
            canDelete={canDelete}
            categoryLabel={categoryLabel}
            onDownload={onDownload}
            onPreview={onPreview}
            onEdit={canEdit ? setEditTarget : null}
            onNewVersion={canEdit ? setVersionTarget : null}
            onHistory={setHistoryTarget}
            onArchive={canArchive ? (d) => setConfirmTarget({ type: 'archive', doc: d }) : null}
            onDelete={canDelete ? (d) => setConfirmTarget({ type: 'delete', doc: d }) : null}
            onShare={setShareTarget}
            onUploadTo={canEdit ? (fid) => { setUploadFolderId(fid); setUploadOpen(true); } : null}
            refresh={loadAll}
          />
        </TabsContent>

        <TabsContent value="expiring" className="pt-4">
          {expiring.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>No documents are expiring in the next 90 days. </AlertDescription>
            </Alert>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Expiry</TableHead>
                      <TableHead>Days left</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiring.map((d) => (
                      <TableRow key={d.id} data-testid={`expiring-row-${d.id}`}>
                        <TableCell className="font-medium">{d.name}</TableCell>
                        <TableCell>{categoryLabel[d.category] || d.category}</TableCell>
                        <TableCell>{d.expiry_date}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={URGENCY_BADGE(d.urgency)}>
                            {d.days_to_expiry} day{d.days_to_expiry === 1 ? '' : 's'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => onDownload(d)}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="archive" className="pt-4">
          <ArchivedDocs onLoad={loadAll} onRestore={canArchive ? onRestore : null} onDelete={canDelete ? (d) => setConfirmTarget({ type: 'delete', doc: d }) : null} categoryLabel={categoryLabel} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {uploadOpen && (
        <UploadDialog
          open={uploadOpen} onClose={() => setUploadOpen(false)}
          categories={categories} onUploaded={() => { setUploadOpen(false); loadAll(); }}
          scopesMeta={scopesMeta} uploadScopes={uploadScopes}
          defaultScope={scope !== 'all' && uploadScopes.includes(scope) ? scope : (uploadScopes[0] || 'general')}
          folderId={uploadFolderId}
          folderName={folders.find((f) => f.id === uploadFolderId)?.name || null}
        />
      )}
      {editTarget && (
        <EditDialog
          doc={editTarget} categories={categories}
          scopesMeta={scopesMeta} uploadScopes={uploadScopes}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); loadAll(); }}
        />
      )}
      {versionTarget && (
        <NewVersionDialog
          doc={versionTarget} onClose={() => setVersionTarget(null)}
          onUploaded={() => { setVersionTarget(null); loadAll(); }}
        />
      )}
      {historyTarget && (
        <VersionHistoryDialog
          doc={historyTarget} onClose={() => setHistoryTarget(null)}
        />
      )}
      {previewTarget && (
        <PreviewDialog
          doc={previewTarget} onClose={() => setPreviewTarget(null)}
          onDownload={() => onDownload(previewTarget)}
        />
      )}
      {shareTarget && (
        <ShareDialog
          doc={shareTarget}
          scopesMeta={scopesMeta}
          uploadScopes={uploadScopes}
          canEdit={canEdit}
          onClose={() => setShareTarget(null)}
          onChanged={() => { setShareTarget(null); loadAll(); }}
        />
      )}
      {confirmTarget && (
        <AlertDialog open onOpenChange={(v) => !v && setConfirmTarget(null)}>
          <AlertDialogContent data-testid="doc-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmTarget.type === 'delete' ? `Permanently delete “${confirmTarget.doc.name}”?` : `Archive “${confirmTarget.doc.name}”?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmTarget.type === 'delete'
                  ? 'The file and all of its versions will be removed. This cannot be undone.'
                  : 'The document will be moved to the Archive tab. You can restore it later.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={confirmTarget.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
                onClick={() => {
                  const { type, doc } = confirmTarget;
                  setConfirmTarget(null);
                  if (type === 'delete') onDelete(doc); else onArchive(doc);
                }}
                data-testid="doc-confirm-action"
              >
                {confirmTarget.type === 'delete' ? 'Delete' : 'Archive'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

// ===========================================================================
const SCOPE_BADGE = {
  schools: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300',
  women: 'bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300',
  general: 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-200',
};
const SCOPE_LABEL = { schools: 'Schools', women: 'Women', general: 'General' };

const ScopeTabs = ({ scope, onChange, scopesMeta, accessibleScopes, byScope }) => {
  if (!accessibleScopes || accessibleScopes.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          You don&apos;t have access to any document scope. Ask your Super Admin to grant access.
        </AlertDescription>
      </Alert>
    );
  }
  const totalAccessible = accessibleScopes.reduce((acc, s) => acc + (byScope?.[s] || 0), 0);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="scope-tabs">
      <Button
        variant={scope === 'all' ? 'default' : 'outline'} size="sm"
        onClick={() => onChange('all')} data-testid="scope-tab-all"
      >
        <FolderOpen className="h-3.5 w-3.5 mr-1.5" /> All accessible
        <Badge variant="secondary" className="ml-2">{totalAccessible}</Badge>
      </Button>
      {scopesMeta
        .filter((s) => s.can_view)
        .map((s) => (
          <Button
            key={s.key}
            variant={scope === s.key ? 'default' : 'outline'} size="sm"
            onClick={() => onChange(s.key)}
            title={s.description}
            data-testid={`scope-tab-${s.key}`}
          >
            {SCOPE_LABEL[s.key] || s.label}
            <Badge variant="secondary" className="ml-2">{byScope?.[s.key] || 0}</Badge>
          </Button>
        ))}
    </div>
  );
};

// ===========================================================================
const StatCard = ({ label, value, icon: Icon, tone, testid }) => {
  const toneClasses = {
    primary: 'text-primary bg-primary/10',
    emerald: 'text-emerald-700 bg-emerald-100',
    amber: 'text-amber-700 bg-amber-100',
    red: 'text-red-700 bg-red-100',
    slate: 'text-slate-700 bg-slate-100',
  };
  return (
    <Card className="card-hover" data-testid={testid}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="text-2xl font-bold">{value}</div>
          </div>
          <div className={`p-2 rounded-md ${toneClasses[tone] || ''}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ===========================================================================
const DocumentsTable = ({
  docs, categoryLabel,
  onDownload, onPreview, onEdit, onNewVersion, onHistory, onArchive, onRestore, onDelete,
}) => {
  if (docs.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center text-muted-foreground">
        No documents match your filters. Upload your first one to get started.
      </CardContent></Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map((d) => {
              const Icon = MIME_ICON(d.file_type);
              return (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium flex items-center gap-1">
                          {d.name}
                          {d.is_quick_form && <Star className="h-3 w-3 text-amber-500 fill-amber-400" title="Quick form" />}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {d.file_name} · {(d.file_size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={SCOPE_BADGE[d.scope] || SCOPE_BADGE.general}>
                      {SCOPE_LABEL[d.scope] || d.scope || 'General'}
                    </Badge>
                  </TableCell>
                  <TableCell>{categoryLabel[d.category] || d.category}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(d.tags || []).slice(0, 3).map((t) => (
                        <Badge key={t} variant="outline" className="text-[10px] py-0">{t}</Badge>
                      ))}
                      {(d.tags || []).length > 3 && (
                        <Badge variant="outline" className="text-[10px] py-0">+{d.tags.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>v{d.version}</TableCell>
                  <TableCell>
                    {d.expiry_date ? (
                      <span className={
                        d.status === 'expired' ? 'text-red-600 font-medium'
                        : (d.days_to_expiry !== null && d.days_to_expiry <= 30) ? 'text-amber-700 font-medium'
                        : ''
                      }>
                        {d.expiry_date}
                      </span>
                    ) : (<span className="text-muted-foreground text-xs">—</span>)}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{new Date(d.updated_at).toLocaleDateString()}</div>
                    <div className="text-muted-foreground">{d.uploaded_by_name}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-0.5">
                      {d.previewable && (
                        <Button size="sm" variant="ghost" onClick={() => onPreview(d)} title="Preview" data-testid={`preview-${d.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onDownload(d)} title="Download" data-testid={`download-${d.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      {onEdit && (
                        <Button size="sm" variant="ghost" onClick={() => onEdit(d)} title="Edit metadata" data-testid={`edit-${d.id}`}>
                          <Edit3 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {onNewVersion && (
                        <Button size="sm" variant="ghost" onClick={() => onNewVersion(d)} title="Upload new version">
                          <Upload className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onHistory(d)} title="Version history">
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      {d.status === 'archived' && onRestore && (
                        <Button size="sm" variant="ghost" onClick={() => onRestore(d)} title="Restore">
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {d.status !== 'archived' && onArchive && (
                        <Button size="sm" variant="ghost" onClick={() => onArchive(d)} title="Archive" data-testid={`archive-${d.id}`}>
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {onDelete && (
                        <Button size="sm" variant="ghost" onClick={() => onDelete(d)} title="Delete" className="text-red-600 hover:bg-red-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ===========================================================================
const ArchivedDocs = ({ onRestore, onDelete, categoryLabel }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/documents`, { params: { status: 'archived' } })
      .then((r) => setRows(r.data || []))
      .catch(() => {});
  }, []);
  return (
    <DocumentsTable
      docs={rows}
      categoryLabel={categoryLabel}
      onDownload={async (d) => {
        const r = await axios.get(`${API_URL}/api/documents/${d.id}/download`);
        downloadBase64(r.data.file_data, r.data.file_name, r.data.file_type);
      }}
      onPreview={() => null}
      onEdit={null}
      onNewVersion={null}
      onHistory={() => null}
      onArchive={null}
      onRestore={onRestore}
      onDelete={onDelete}
    />
  );
};

// ===========================================================================
const UploadDialog = ({ open, onClose, categories, onUploaded, scopesMeta, uploadScopes, defaultScope, folderId, folderName }) => {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: '', category: 'forms', scope: defaultScope || 'general',
    description: '', tags: '', expiry_date: '', is_quick_form: false,
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const onPickFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10 MB limit');
      return;
    }
    setFile(f);
    if (!form.name) setForm((s) => ({ ...s, name: f.name.replace(/\.[^/.]+$/, '') }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Choose a file'); return; }
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (!uploadScopes.includes(form.scope)) {
      toast.error(`You don't have upload access to "${SCOPE_LABEL[form.scope] || form.scope}"`);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', form.name);
      fd.append('category', form.category);
      fd.append('scope', form.scope);
      if (form.description) fd.append('description', form.description);
      if (form.tags) fd.append('tags', form.tags);
      if (form.expiry_date) fd.append('expiry_date', form.expiry_date);
      fd.append('is_quick_form', form.is_quick_form ? 'true' : 'false');
      if (folderId) fd.append('folder_id', folderId);
      await axios.post(`${API_URL}/api/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded');
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const uploadableScopes = (scopesMeta || []).filter((s) => uploadScopes.includes(s.key));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload document</DialogTitle>
          <DialogDescription>Max 10 MB. Pick the scope first — categories help others find it later.</DialogDescription>
        </DialogHeader>
        {folderName && (
          <div className="flex items-center gap-2 text-sm bg-muted/60 rounded-md px-3 py-2" data-testid="upload-folder-context">
            <FolderOpen className="h-4 w-4 text-amber-500" />
            Uploading into folder: <span className="font-medium">{folderName}</span>
          </div>
        )}
        {uploadableScopes.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>You don&apos;t have upload access to any scope. Ask your Super Admin.</AlertDescription>
          </Alert>
        ) : (
        <form onSubmit={submit} className="space-y-3">
          {/* Scope picker — first thing, so users frame the upload by scope */}
          <div>
            <Label>Scope *</Label>
            <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
              <SelectTrigger data-testid="upload-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                {uploadableScopes.map((s) => (
                  <SelectItem key={s.key} value={s.key} data-testid={`upload-scope-option-${s.key}`}>
                    {SCOPE_LABEL[s.key] || s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => { e.preventDefault(); setIsDragging(false); onPickFile(e.dataTransfer.files?.[0]); }}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
            data-testid="upload-drop-zone"
          >
            <input
              ref={fileRef} type="file" className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0])}
              data-testid="upload-file-input"
            />
            {file ? (
              <div className="text-sm">
                <FileText className="h-8 w-8 mx-auto text-primary mb-1" />
                <div className="font-medium">{file.name}</div>
                <div className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <>
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-1" />
                <div className="text-sm font-medium">Click or drop a file</div>
                <div className="text-xs text-muted-foreground">PDF, Word, Excel, images, etc.</div>
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="upload-name" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="policy, hr, 2026" data-testid="upload-tags" />
            </div>
            <div>
              <Label>Expiry date (optional)</Label>
              <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="upload-expiry" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.is_quick_form} onCheckedChange={(v) => setForm({ ...form, is_quick_form: !!v })} data-testid="upload-quick-form" />
            <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-amber-500" /> Pin to Quick Forms bar (one-click download)</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting || !file} data-testid="upload-submit">
              {submitting ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ===========================================================================
const EditDialog = ({ doc, categories, scopesMeta, uploadScopes, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: doc.name,
    category: doc.category,
    scope: doc.scope || 'general',
    description: doc.description || '',
    tags: (doc.tags || []).join(', '),
    expiry_date: doc.expiry_date || '',
    is_quick_form: !!doc.is_quick_form,
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/documents/${doc.id}`, {
        name: form.name,
        category: form.category,
        scope: form.scope,
        description: form.description || null,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        expiry_date: form.expiry_date || null,
        is_quick_form: form.is_quick_form,
      });
      toast.success('Updated');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };
  const moveScopes = (scopesMeta || []).filter((s) => uploadScopes.includes(s.key));
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit metadata</DialogTitle>
          <DialogDescription>Update info without replacing the file. To change the file, use &quot;New version&quot;.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label>Scope</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger data-testid="edit-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {moveScopes.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{SCOPE_LABEL[s.key] || s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Expiry</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
          <div>
            <Label>Tags</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.is_quick_form} onCheckedChange={(v) => setForm({ ...form, is_quick_form: !!v })} />
            <span>Pin to Quick Forms</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ===========================================================================
const NewVersionDialog = ({ doc, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Choose a file');
    if (file.size > 10 * 1024 * 1024) return toast.error('File exceeds 10 MB');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (note) fd.append('note', note);
      const r = await axios.post(`${API_URL}/api/documents/${doc.id}/version`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded v${r.data.version}`);
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload new version — {doc.name}</DialogTitle>
          <DialogDescription>Current: v{doc.version}. The previous version is preserved in history.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input type="file" onChange={(e) => setFile(e.target.files?.[0])} />
          <div><Label>What changed? (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. corrected typo on page 3" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting || !file}>{submitting ? 'Uploading…' : 'Upload v' + (doc.version + 1)}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ===========================================================================
const VersionHistoryDialog = ({ doc, onClose }) => {
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/documents/${doc.id}/versions`).then((r) => setVersions(r.data || [])).catch(() => {});
  }, [doc.id]);
  const downloadVersion = async (v) => {
    const r = await axios.get(`${API_URL}/api/documents/${doc.id}/versions/${v}/download`);
    downloadBase64(r.data.file_data, r.data.file_name, r.data.file_type);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Version history — {doc.name}</DialogTitle>
          <DialogDescription>Current: v{doc.version} · {versions.length} previous version{versions.length !== 1 ? 's' : ''}</DialogDescription>
        </DialogHeader>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Version</TableHead><TableHead>Uploaded</TableHead><TableHead>By</TableHead><TableHead>Note</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {versions.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Only the current version exists.</TableCell></TableRow>}
            {versions.map((v) => (
              <TableRow key={v.id}>
                <TableCell>v{v.version}</TableCell>
                <TableCell className="text-xs">{new Date(v.uploaded_at).toLocaleString()}</TableCell>
                <TableCell className="text-xs">{v.uploaded_by_name || '—'}</TableCell>
                <TableCell className="text-xs">{v.note || '—'}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => downloadVersion(v.version)}>
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

// ===========================================================================
const ShareDialog = ({ doc, scopesMeta, uploadScopes, canEdit, onClose, onChanged }) => {
  const [link, setLink] = useState('');
  const [scopeSel, setScopeSel] = useState(doc.scope || 'general');
  const [busy, setBusy] = useState(false);

  const generateLink = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/documents/${doc.id}/share`);
      const url = `${API_URL}/api/documents/shared/${r.data.token}`;
      setLink(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied to clipboard');
      } catch {
        toast.info('Link generated — copy it below');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create share link');
    } finally { setBusy(false); }
  };

  const applyScope = async () => {
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/documents/${doc.id}`, { scope: scopeSel });
      toast.success(`Now visible to the ${SCOPE_LABEL[scopeSel] || scopeSel} group`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update sharing');
    } finally { setBusy(false); }
  };

  const shareableScopes = (scopesMeta || []).filter((s) => uploadScopes.includes(s.key));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="h-4 w-4" /> Share — {doc.name}</DialogTitle>
          <DialogDescription>Get a download link, or change which team can see this file.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Download link</Label>
            {link ? (
              <div className="flex gap-2">
                <Input readOnly value={link} className="text-xs" data-testid="share-link-input"
                  onFocus={(e) => e.target.select()} />
                <Button variant="outline" size="sm" onClick={async () => {
                  try { await navigator.clipboard.writeText(link); toast.success('Copied'); }
                  catch { toast.info('Select and copy the link manually'); }
                }} data-testid="share-copy-btn">Copy</Button>
              </div>
            ) : (
              <Button variant="outline" className="w-full" onClick={generateLink} disabled={busy} data-testid="share-generate-link-btn">
                <Link2 className="h-4 w-4 mr-2" /> {busy ? 'Generating…' : 'Copy download link'}
              </Button>
            )}
            <p className="text-xs text-muted-foreground">Anyone with this link can download the file — no login needed.</p>
          </div>
          {canEdit && shareableScopes.length > 0 && (
            <div className="space-y-2 border-t pt-3">
              <Label>Share with a group</Label>
              <div className="flex gap-2">
                <Select value={scopeSel} onValueChange={setScopeSel}>
                  <SelectTrigger data-testid="share-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {shareableScopes.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{SCOPE_LABEL[s.key] || s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={applyScope} disabled={busy || scopeSel === (doc.scope || 'general')} data-testid="share-scope-apply">
                  Apply
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Currently visible to: <Badge variant="outline" className="text-[10px] py-0">{SCOPE_LABEL[doc.scope] || 'General'}</Badge>
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===========================================================================
const PreviewDialog = ({ doc, onClose, onDownload }) => (
  <Dialog open onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="max-w-4xl max-h-[90vh]">
      <DialogHeader>
        <DialogTitle>{doc.name} (v{doc.version})</DialogTitle>
        <DialogDescription>{doc.file_name} · {(doc.file_size / 1024).toFixed(1)} KB</DialogDescription>
      </DialogHeader>
      <div className="bg-muted rounded-md overflow-hidden" style={{ height: '70vh' }}>
        {doc.file_type?.startsWith('image/') ? (
          <img src={doc.dataUrl} alt={doc.name} className="w-full h-full object-contain bg-white" />
        ) : (
          <embed src={doc.dataUrl} type={doc.file_type} className="w-full h-full" />
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={onDownload}><Download className="h-4 w-4 mr-2" /> Download</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default DocumentsPage;
